"use client";

import Navbar from "@/components/Navbar";
import BottomCta from "@/components/BottomCta";
import Link from "next/link";
import { motion } from "framer-motion";
import { Check, Shield, TrendingUp, FileCheck, FileText, ShieldCheck, Clock3, CheckCircle2 } from "lucide-react";

export default function CorporateTaxPage() {
  return (
    <main className="bg-[#FAFAFA] min-h-screen w-full overflow-x-hidden text-[#000000]">
      <Navbar />

      {/* ── HERO SECTION ── */}
      <section className="relative min-h-[85vh] flex items-center justify-center py-20 lg:py-32 2xl:py-48 overflow-hidden">
        <div className="max-w-4xl xl:max-w-6xl 2xl:max-w-7xl mx-auto w-full px-6 text-center flex flex-col items-center relative z-10">

          <motion.div
            initial={{ opacity: 0, y: -15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full border border-[#B8893A]/20 bg-[#B8893A]/5 mb-8"
          >
            <Shield size={14} className="text-[#B8893A]" />
            <span className="text-[10px] sm:text-xs font-bold text-[#B8893A] uppercase tracking-[0.2em]">
              Tax Compliance Experts
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="text-4xl sm:text-5xl md:text-6xl xl:text-7xl 2xl:text-8xl font-bold tracking-tight leading-[1.15] mb-6 max-w-5xl text-[#000000]"
          >
            VAT & Tax Advisory <br />
            <span className="font-serif font-normal text-[#B8893A]">in the UAE</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="text-gray-500 text-base sm:text-lg md:text-xl xl:text-2xl max-w-2xl xl:max-w-4xl font-light leading-relaxed mb-10"
          >
            We provide practical consultancy and hands-on support to help your business stay compliant, avoid risks, and manage taxes with clarity and confidence.
          </motion.p>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
            <a
              href="https://wa.me/971507987470"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex w-full sm:w-auto items-center justify-center px-10 py-4 xl:px-14 xl:py-5 font-bold text-white bg-[#B8893A] rounded-xl transition-all duration-300 hover:bg-[#a3762f] hover:scale-[1.03] active:scale-[0.98] text-base xl:text-lg tracking-wide"
            >
              Book a Consultation
            </a>
            <Link href="/services" className="group inline-flex w-full sm:w-auto items-center justify-center px-10 py-4 xl:px-14 xl:py-5 font-bold text-[#B8893A] bg-transparent border-2 border-[#B8893A] rounded-xl transition-all duration-300 hover:bg-[#B8893A]/5 hover:scale-[1.03] active:scale-[0.98] text-base xl:text-lg tracking-wide">
              View All Services
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── STATS SECTION ── */}

      {/* ── BENEFITS ── */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 xl:py-32 bg-[#FAFAFA]">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl xl:text-5xl font-black tracking-tight mb-4">Why VAT & Tax Matters</h2>
            <p className="text-gray-400 font-medium xl:text-lg  tracking-wider text-xs">Proper tax handling protects your business and keeps your operations running smoothly.</p>
            <div className="w-12 h-[3px] bg-[#B8893A] mx-auto mt-4 rounded-full" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 xl:gap-8">
            {[
              { icon: Shield, title: "Stay Compliant", text: "Meet UAE VAT and tax regulations with confidence." },
              { icon: TrendingUp, title: "Avoid Penalties", text: "Prevent unexpected costs and legal issues." },
              { icon: FileCheck, title: "Accurate Records", text: "Maintain organized financial documentation." },
            ].map((item, i) => {
              const IconComponent = item.icon;
              return (
                <div key={i} className="group bg-white p-8 rounded-2xl border border-gray-100/80 transition-all duration-300 hover:shadow-xl shadow-md flex flex-col justify-between">
                  <div>
                    <div className="w-10 h-10 rounded-xl bg-[#B8893A]/5 flex items-center justify-center text-[#B8893A] mb-6 group-hover:bg-[#B8893A] group-hover:text-white transition-all duration-300">
                      <IconComponent className="w-5 h-5" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 group-hover:text-[#B8893A] transition-colors duration-300">{item.title}</h3>
                    <p className="text-gray-500 font-light text-sm leading-relaxed">{item.text}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CORPORATE TAX SERVICES (APPROACH) ── */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 xl:py-32 bg-white border-t border-gray-100">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-3xl sm:text-4xl xl:text-5xl font-black tracking-tight mb-4">Corporate Tax Services</h2>
            <p className="text-gray-400 font-medium xl:text-lg tracking-wider text-xs">Complete compliance and advisory support for UAE businesses</p>
            <div className="w-12 h-[3px] bg-[#B8893A] mx-auto mt-4 rounded-full" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8">
            {[
              { num: "01", title: "Understanding Your Business", text: "We review your activities and financial setup." },
              { num: "02", title: "Assessment", text: "We identify your VAT and tax obligations clearly." },
              { num: "03", title: "Setup & Compliance", text: "We assist with registration, filings, and documentation." },
              { num: "04", title: "Ongoing Support", text: "We ensure everything stays updated and compliant." },
            ].map((step, i) => (
              <div
                key={i}
                className="relative group bg-white p-5 rounded-2xl border border-gray-100 shadow-[0_10px_35px_rgba(0,0,0,0.08)] hover:shadow-[0_20px_50px_rgba(184,137,58,0.15)] hover:-translate-y-2 transition-all duration-300"
              >
                <div className="text-6xl xl:text-7xl font-black text-gray-100 group-hover:text-[#B8893A]/10 transition-colors duration-300 mb-4">
                  {step.num}
                </div>

                <h3 className="text-lg font-bold mb-2 tracking-tight">
                  {step.title}
                </h3>

                <p className="text-gray-500 font-light text-sm leading-relaxed">
                  {step.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY CHOOSE US & FEATURES ── */}
      <section className="px-6 sm:px-12 lg:px-16 py-20 bg-[#FAFAFA] border-t border-gray-100">
        <div className="max-w-[1600px] mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <span className="text-xs font-bold text-[#B8893A] uppercase tracking-widest mb-2">Why Choose Us</span>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4">Support you can depend on</h2>
            <p className="text-gray-500 font-light leading-relaxed mb-6">We provide clear, reliable tax and VAT services designed to reduce risk, save time, and keep your UAE business fully compliant without the confusion.</p>
            <div className="flex flex-col gap-4">
              {[
                {
                  title: "Clear explanations without technical confusion",
                  text: "We translate complex tax requirements into simple, actionable steps.",
                },
                {
                  title: "Accurate and timely filing support",
                  text: "Your returns are prepared correctly and submitted on schedule, every time.",
                },
                {
                  title: "Strong understanding of UAE tax laws",
                  text: "Deep expertise in VAT, corporate tax, and local compliance requirements.",
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="bg-white p-5 rounded-2xl border border-gray-100 shadow-[0_8px_30px_rgba(0,0,0,0.08)] hover:shadow-[0_15px_40px_rgba(0,0,0,0.12)] transition-all duration-300"
                >
                  <h4 className="font-bold text-[#0D0C09] text-sm mb-2">
                    {item.title}
                  </h4>
                  <p className="text-sm text-gray-500 leading-relaxed">
                    {item.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {[
              "VAT Registration & De-registration",
              "VAT Return Filing & Compliance",
              "Tax Planning & Advisory",
              "Audit Support & Documentation",
              "Penalty Assessment & Resolution",
              "Cross-border Tax Consulting"
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-4 border border-gray-100 bg-white rounded-xl">
                <CheckCircle2 className="w-4 h-4 text-[#B8893A] shrink-0 mt-0.5" />
                <span className="text-sm font-semibold text-gray-700">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BottomCta
        title="Stay Compliant. Stay Prepared."
        subtitle="Contact us today and take control of your tax responsibilities."
      />

    </main>
  );
}
